import { createApp } from 'vue'
import { createPinia } from 'pinia'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import AppShell from './app-shell/AppShell.vue'
import router from './router'

import './styles/style.scss'
import i18n from './i18n'
import authService from './api/authService'

// Import the Auth0 configuration
import authConfig from '../auth_config.json'
import { Auth0Plugin, setSharedPlanTokenCartoV3 } from './auth'
import type { RouteLocationRaw } from 'vue-router'
import { getRedirectUri, getRequireComponent } from './constants'
import { initializeFirebaseApp, AuthManager } from '@workspaces/services'
import BrowserCartoAuthorizer from '@/auth/carto-auth'
import { getAppMetadata } from './plan/metadata/metadata'
import {
  getAccessTokenForSharedPlan,
  getBasemapsConfig,
  getCartoAppConfiguration,
  getCartoConfigurationSSO,
  getLayersPrecedence,
} from './plan/metadata/metadata.helper'
import { TrackJS } from 'trackjs'
import BrowserEnvironmentResolver from './helpers/environment.helper'
import { initializeUserPermissionManager } from '@/helpers/auth-browser.helper'
import SharedPlanInfoManager from '@/helpers/sharedPlanInfo.helper'
import { Loader } from '@googlemaps/js-api-loader'
import { isGoogleMapsBasemap } from './helpers/google-maps.helper'
import { BasemapApiKey } from '@workspaces/types/src/basemap.type'
import { setupZIndexValuesForLayers } from '@/layers/layers-z-index.helper'
;(async () => {
  try {
    console.debug('🟩 Preparing APP ...')
    const environmentResolver = BrowserEnvironmentResolver.getInstance()
    const isTesting = environmentResolver.isTesting()
    console.debug(`😈 Testing mode: ${isTesting}`)
    const metadata = getAppMetadata()
    window.document.title = metadata.app_title

    // TrackJS configuration ----------
    const trackJsToken = import.meta.env.VITE_APP_TRACKJS_TOKEN
    if (
      isTesting ||
      location.host.indexOf('localhost') === 0 ||
      location.host.indexOf('127.0.0.1') === 0
    ) {
      console.debug('🟩 TrackJS: disabled')
    } else if (!trackJsToken) {
      console.warn('🟧 TrackJS: disabled (VITE_APP_TRACKJS_TOKEN is not set)')
      throw new Error('VITE_APP_TRACKJS_TOKEN is not set')
    } else {
      const version = `${import.meta.env.VITE_APP_ENV}-${metadata.flavour}`
      console.debug(`🟩 TrackJS: enabled | version: ${version}`)
      TrackJS.install({
        token: trackJsToken,
        application: 'carto-ps-adastra-eu',
        console: { display: true, watch: ['error', 'warn'] },
        network: { error: false },
        version,
      })
    }

    const layersPrecedence = getLayersPrecedence(metadata)
    console.debug(
      '🗺️ Setting up layers precendece, from top to bottom: ',
      layersPrecedence,
    )
    setupZIndexValuesForLayers(layersPrecedence)

    // Load Google Maps API
    const basemapConfig = getBasemapsConfig(metadata)
    if (basemapConfig) {
      const googleMapsIsAvailable = basemapConfig.options.find((e) =>
        isGoogleMapsBasemap(e.id),
      )
      if (googleMapsIsAvailable) {
        console.debug('🟩 Google Maps: loading ...')
        if (!googleMapsIsAvailable.apiKey) {
          throw new Error(
            'Google Maps API key is required, as it is configured as a basemap. Check metadata file',
          )
        }
        const environment =
          environmentResolver.getEnvironment() as keyof BasemapApiKey
        const loader = new Loader({
          apiKey: googleMapsIsAvailable.apiKey[environment],
          version: 'weekly',
          language: 'en',
          libraries: ['places'],
        })

        await loader.importLibrary('maps')
        console.debug('🟩 Google Maps: loaded')
      }
    }

    // Carto configuration ---------
    const cartoAppConfiguration = getCartoAppConfiguration(
      metadata,
      environmentResolver,
    )
    console.debug('🟩 CARTO app configuration', cartoAppConfiguration)

    // Parse the query string, NOT the full href: `new URLSearchParams(href)`
    // folds the URL prefix into the first parameter's key (e.g. the key becomes
    // `https://host/plan?shared`), so `get('shared')` — the first param in a
    // share link `?shared=true&planId=...` — returns null and the shared-plan
    // branch is never taken.
    const queryParams = new URLSearchParams(window.location.search)
    const sharedPlan = queryParams.get('shared')

    // Create the Vue 3 app and register the core plugins. Pinia must be
    // installed before the auth flow / login, which reach the stores via
    // useXStore().
    const app = createApp(AppShell)
    app.use(createPinia())
    app.use(i18n)

    // Auth0 configuration ---------
    if (sharedPlan !== 'true') {
      console.debug('🟧 Auth0: registering plugin')
      const redirectUri = getRedirectUri()
      app.use(Auth0Plugin, {
        domain: authConfig.domain,
        clientId: cartoAppConfiguration.clientId,
        redirectUri,
        cacheLocation: authConfig.cacheLocation,
        scopes: authConfig.scopes,
        audience: authConfig.audience,
        organization: getCartoConfigurationSSO(metadata, environmentResolver),
        onRedirectCallback: (appState: { targetUrl: RouteLocationRaw }) => {
          router.push(
            appState && appState.targetUrl
              ? appState.targetUrl
              : window.location.pathname,
          )
        },
      })
    } else {
      console.debug('🟩 Auth0: Not registering plugin [Shared plan]')
      const sharedPlanId = queryParams.get('planId')
      if (sharedPlanId === null) {
        throw new Error('Shared plan id is required')
      }
      const sharedPlanInfoManager = SharedPlanInfoManager.getInstance()
      await sharedPlanInfoManager.init(
        metadata,
        environmentResolver,
        sharedPlanId,
      )

      // deck.gl 9 removed the global setDefaultCredentials that used to carry
      // the share token. Every CARTO source now reads its token from the auth
      // service (AuthManager.getToken -> getAccessTokenCartoV3), so store the
      // share token there for the anonymous shared-plan session.
      const shareTileToken = getAccessTokenForSharedPlan(environmentResolver)
      if (shareTileToken) {
        setSharedPlanTokenCartoV3(shareTileToken)
      }

      // The authenticated flows init AuthManager (authService.login, Auth0
      // callback, Plan/Dashboard mount). Shared-plan mode runs none of those,
      // yet deck.gl 9 sources call AuthManager.getToken() as soon as they
      // mount. Initialize it here (idempotent — first authorizer wins) so the
      // share token is served before any component mounts.
      AuthManager.getInstance().init(new BrowserCartoAuthorizer())
    }

    // Airship `<as-*>` custom elements are declared in vite.config.ts
    // (compilerOptions.isCustomElement), replacing Vue.config.ignoredElements.

    // Error handler
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    window.onerror = (message, source, lineno, colno, error) => {
      // message: error message (string). Available as event (sic!) in HTML onerror="" handler.
      // source: URL of the script where the error was raised (string)
      // lineno: Line number where error was raised (number)
      // colno: Column number for the line where the error occurred (number)
      // error: Error Object (object)
      onError({
        err: message,
        source,
        info: `in line ${lineno}:${colno}`,
      })
    }

    app.config.errorHandler = (err, instance, info) => {
      // err: error trace
      // instance: component in which error occurred
      // info: Vue specific error information such as lifecycle hooks, events etc.
      onError({
        err,
        source: instance,
        info,
      })
    }

    const requireComponent = getRequireComponent()

    Object.entries(requireComponent).forEach(([fileName, component]) => {
      // Get component config
      const componentConfig = component as { default?: unknown }
      const fileNameSplitted = fileName.split('/')
      const chunk = fileNameSplitted.pop() || ''
      const componentName = chunk.replace(/\.\w+$/, '')

      // Register component globally
      app.component(componentName, componentConfig.default || componentConfig)
    })

    // Firebase configuration ---------

    initializeFirebaseApp(metadata, environmentResolver)

    if (sharedPlan !== 'true') {
      console.debug('🟧 Login ...')
      await authService.login()
    } else {
      console.debug('🟧 Login: Not logging in [Shared plan]')
      await initializeUserPermissionManager()
    }

    // Running Vue App ---------

    // Install the router last: vue-router runs the initial navigation (and its
    // beforeEach guard) as soon as it is installed, so the auth state restored
    // by authService.login() above must already exist or a hard reload to an
    // authed route would bounce to /login.
    app.use(router)
    app.mount('#app')

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    function onError({ err, source, info }) {
      /* eslint-disable */
      console.error('Error: ', err.message, err)
      console.error('Source: ', source)
      console.error('Extra info: ', info)
      /* eslint-enable */
    }
  } catch (err) {
    console.error(err)
    process.exit(1)
  }
})()
